import { Injectable } from '@angular/core';
import { ICollectionResponse } from '../../admin/sync-monitoring/sync-monitoring.models';
import { parseCollection, parseResponseItem } from '../../admin/sync-monitoring/zod-parse';
import { CrudService } from '@shared/services/crud.service';
import { IAccount, UserService } from '@shared/services/user.service';
import { map, Observable, of, switchMap } from 'rxjs';
import { IMonitoringErrorRow, IMonitoringErrorsParams, IMonitoringOverview, MonitoringErrorRowSchema, MonitoringOverviewSchema } from './monitoring.models';

/** Reads core-one's cross-account monitoring endpoints (fn-19). They are not account-scoped, but Core One's authorizer still needs an `x-account-id` header, so the overview sends the selected account, or the user's first account when none is selected, and an environment's errors send that environment's account. */
@Injectable({ providedIn: 'root' })
export class MonitoringService {
	constructor(
		private crudService: CrudService,
		private userService: UserService,
	) {}

	/** GET /one/monitoring/overview: every failing environment of every account, in one response. */
	getOverview(): Observable<IMonitoringOverview> {
		const endpoint = '/one/monitoring/overview';
		return this.crudService.get<unknown>(endpoint, this.userService.account ?? this.userService.accountsSubject$.value[0]).pipe(map((response) => parseResponseItem(MonitoringOverviewSchema, response, endpoint)));
	}

	/** GET /one/monitoring/errors for one environment, following `next` (sent back as `after`) until the last page, so the result is every row or an error, never part of the rows. An empty `kind` list matches nothing, so it returns no rows without a request. */
	getEnvironmentErrors(accountId: string, environmentId: string, params: IMonitoringErrorsParams): Observable<IMonitoringErrorRow[]> {
		if (params.kind?.length === 0) {
			return of([]);
		}
		const readFrom = (after?: string): Observable<IMonitoringErrorRow[]> => {
			const query = new URLSearchParams({ accountId, environmentId });
			if (params.terminal !== undefined) {
				query.append('terminal', String(params.terminal));
			}
			if (params.includeOutdated !== undefined) {
				query.append('includeOutdated', String(params.includeOutdated));
			}
			params.kind?.forEach((kind) => query.append('kind', kind));
			if (after) {
				query.append('after', after);
			}
			const endpoint = `/one/monitoring/errors?${query}`;
			return this.crudService.get<ICollectionResponse<unknown> & { next?: string }>(endpoint, { id: accountId } as IAccount).pipe(
				switchMap((response) => {
					const rows = parseCollection(MonitoringErrorRowSchema, response, endpoint);
					return response.next ? readFrom(response.next).pipe(map((rest) => [...rows, ...rest])) : of(rows);
				}),
			);
		};
		return readFrom();
	}
}
